import React, { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { Box, Button, Divider, MenuItem, Stack, TextField } from '@mui/material';

import {
  BeddingConfiguration,
  ExtraGuestGeneratedPolicies,
  ExtraGuestGeneratedPolicyItem,
  IncludedGuestEntry,
  RoomPolicy,
} from '~/types/roomPolicy';

import OffersService from '../../../services/OffersService';
import { reportError } from '../../../utils/reportError';
import ErrorListDisplay from '../ErrorListDisplay';

import RoomPolicyBeddingEditor from './RoomPolicyBeddingEditor';
import { RoomPolicyLanguage } from './RoomPolicyForm';
import RoomPolicyGuestTable from './RoomPolicyGuestTable';
import RoomPolicyIncludedGuestsTable from './RoomPolicyIncludedGuestsTable';
import RoomPolicyListEditor from './RoomPolicyListEditor';
import { buttonMessages, buttonStates } from './states/submitButton';

interface RoomPolicyTableEditorProps {
  policyData: ExtraGuestGeneratedPolicies;
  onPolicyDataChange: (data: ExtraGuestGeneratedPolicies) => void;
  roomPolicy: RoomPolicy;
  idOffer: string;
  onGenerateDefault?: () => Promise<void>;
  isGenerating?: boolean;
  isLoadingTranslation?: boolean;
  onRevertToTextInput?: () => void;
  language?: RoomPolicyLanguage;
  onLanguageChange?: (language: RoomPolicyLanguage) => void;
  onRoomPolicyIdChange?: (roomPolicyId: number) => void;
}

export default function RoomPolicyTableEditor({
  policyData,
  onPolicyDataChange,
  roomPolicy,
  idOffer,
  onGenerateDefault,
  isGenerating = false,
  isLoadingTranslation = false,
  onRevertToTextInput,
  language = 'en',
  onLanguageChange,
  onRoomPolicyIdChange,
}: RoomPolicyTableEditorProps) {
  const { enqueueSnackbar } = useSnackbar();

  const [apiErrors, setApiErrors] = useState<Array<string>>([]);
  const [saveState, setSaveState] = useState(buttonStates.default);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  const handleCapacitiesChange = useCallback(
    (items: Array<IncludedGuestEntry>) => {
      onPolicyDataChange({ ...policyData, maximumRoomCapacities: items });
      setSaveState(buttonStates.default);
    },
    [policyData, onPolicyDataChange],
  );

  const handlePackageInclusionsChange = useCallback(
    (items: Array<IncludedGuestEntry>) => {
      onPolicyDataChange({ ...policyData, packageInclusions: items });
      setSaveState(buttonStates.default);
    },
    [policyData, onPolicyDataChange],
  );

  const handleGuestItemsChange = useCallback(
    (items: Array<ExtraGuestGeneratedPolicyItem>) => {
      onPolicyDataChange({ ...policyData, extraGuestGeneratedPolicyItems: items });
      setSaveState(buttonStates.default);
    },
    [policyData, onPolicyDataChange],
  );

  const handleBeddingConfigurationChange = useCallback(
    (value: BeddingConfiguration) => {
      onPolicyDataChange({ ...policyData, beddingConfiguration: value });
      setSaveState(buttonStates.default);
    },
    [policyData, onPolicyDataChange],
  );

  const handleBeddingExtrasChange = useCallback(
    (items: Array<BeddingConfiguration>) => {
      onPolicyDataChange({ ...policyData, beddingConfigurationExtras: items });
      setSaveState(buttonStates.default);
    },
    [policyData, onPolicyDataChange],
  );

  const handleNotesChange = useCallback(
    (items: Array<string>) => {
      onPolicyDataChange({ ...policyData, additionalPolicyNotes: items });
      setSaveState(buttonStates.default);
    },
    [policyData, onPolicyDataChange],
  );

  const handleSave = useCallback(async () => {
    setSaveState(buttonStates.saving);

    const serializedDescription = JSON.stringify(policyData);
    const submitData = {
      room_type_id: roomPolicy.room_type_id,
      description: serializedDescription,
    };

    try {
      if (roomPolicy.id) {
        const { room_type_id: _, ...updateData } = submitData;
        await OffersService.updateRoomPolicy(updateData, idOffer, roomPolicy.id, language);
      } else {
        const response = await OffersService.createRoomPolicy(submitData, idOffer, 'en');
        const createdId =
          typeof response?.result?.id === 'number'
            ? response.result.id
            : typeof response?.result?.room_policy_id === 'number'
            ? response.result.room_policy_id
            : typeof response?.result?.roomPolicyId === 'number'
            ? response.result.roomPolicyId
            : null;
        if (createdId) {
          onRoomPolicyIdChange?.(createdId);
        }
      }
      setSaveState(buttonStates.saved);
      setApiErrors([]);
      enqueueSnackbar('Room policy saved successfully', { variant: 'success' });
    } catch (e) {
      const newApiErrors: Array<string> = [];
      if (e.name === 'ValidationError') {
        e.errors.forEach((err) => {
          newApiErrors.push(err.message);
        });
      }
      setSaveState(buttonStates.failed);
      setApiErrors(newApiErrors);
      reportError(e);
      enqueueSnackbar('Failed to save room policy', { variant: 'error' });
    }
  }, [policyData, roomPolicy, idOffer, enqueueSnackbar, language, onRoomPolicyIdChange]);

  return (
    <Box>
      <Stack spacing={3} divider={<Divider />}>
        <RoomPolicyIncludedGuestsTable
          title="Maximum Room Capacities"
          addButtonLabel="Add capacity"
          items={policyData.maximumRoomCapacities ?? []}
          onChange={handleCapacitiesChange}
        />

        <RoomPolicyIncludedGuestsTable
          title="Package Inclusions"
          addButtonLabel="Add included guest"
          items={policyData.packageInclusions ?? []}
          onChange={handlePackageInclusionsChange}
        />

        <RoomPolicyGuestTable
          items={policyData.extraGuestGeneratedPolicyItems ?? []}
          onChange={handleGuestItemsChange}
          showValidationErrors={hasAttemptedSave}
        />

        <RoomPolicyBeddingEditor
          beddingConfiguration={policyData.beddingConfiguration ?? { bedding: '', icon: '' }}
          beddingExtras={policyData.beddingConfigurationExtras ?? []}
          onBeddingConfigurationChange={handleBeddingConfigurationChange}
          onBeddingExtrasChange={handleBeddingExtrasChange}
        />

        <RoomPolicyListEditor
          title="Additional Policy Notes"
          items={policyData.additionalPolicyNotes ?? []}
          onChange={handleNotesChange}
        />
      </Stack>

      {apiErrors.length > 0 && <ErrorListDisplay messages={apiErrors} />}

      <Box sx={{ display: 'flex', gap: 2, mt: 3, alignItems: 'center', width: '100%' }}>
        <Button
          variant="contained"
          className={saveState}
          onClick={() => {
            setHasAttemptedSave(true);
            handleSave();
          }}
        >
          {buttonMessages[saveState]}
        </Button>
        {onGenerateDefault && (
          <Button
            variant="contained"
            startIcon={<AutoFixHighIcon />}
            onClick={onGenerateDefault}
            disabled={isGenerating}
          >
            {isGenerating ? 'Syncing...' : 'Sync Room Policy'}
          </Button>
        )}
        {!!onRevertToTextInput && (
          <Button variant="text" onClick={onRevertToTextInput} disabled={isGenerating}>
            Revert to text input
          </Button>
        )}
        {!!onLanguageChange && (
          <TextField
            select
            size="small"
            label="Language"
            value={language}
            onChange={(e) => onLanguageChange(e.target.value as 'en' | 'de' | 'fr')}
            disabled={isLoadingTranslation}
            sx={{ minWidth: 200, ml: 'auto' }}
          >
            <MenuItem value="en">English</MenuItem>
            <MenuItem value="de">German</MenuItem>
            <MenuItem value="fr">French</MenuItem>
          </TextField>
        )}
      </Box>
    </Box>
  );
}
